import { useEffect, useRef, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Tiny floating "Live" indicator for admin pages.
 * Pings `/api/health` every 30s (5s when previously failed for fast recovery).
 * Shows:
 *   • Green dot + "Live"  when backend is reachable
 *   • Yellow dot + "Slow" when ping took >2.5s (warns of edge degradation)
 *   • Red dot + "Offline" when 2 consecutive pings failed
 *
 * Crucial for admin UX: when an admin sees "Error loading X" they previously
 * had no way to tell if it was a one-off API blip OR the whole backend down.
 * The indicator tells them in 2 seconds: stop refreshing, the system is up.
 */
export function LiveStatusIndicator() {
  const [status, setStatus] = useState("checking"); // checking | live | slow | offline
  const [latency, setLatency] = useState(null);
  const [lastOk, setLastOk] = useState(null);
  const failuresRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let timer;
    let keepAliveTimer;

    // KEEP-ALIVE PING: on production, the serverless container scales to zero
    // after idle which causes a 5-15s cold-start on the next request. This
    // background ping every 4 minutes keeps the container warm so admins never
    // see the "offline → online" flicker. Fire-and-forget; ignored failures.
    const keepAlivePing = () => {
      fetch(`${API}/health`, { cache: "no-store", keepalive: true }).catch(() => {});
      keepAliveTimer = setTimeout(keepAlivePing, 4 * 60 * 1000);
    };
    keepAlivePing();

    const ping = async () => {
      const start = performance.now();
      let nextStatus = "live";
      let measured = null;
      try {
        const controller = new AbortController();
        // Generous 12s timeout: production serverless cold-starts can take
        // 5-15s. Anything longer than that and the host really IS slow.
        // Previously 5s — that misread every cold-start as "offline".
        const to = setTimeout(() => controller.abort(), 12000);
        const r = await fetch(`${API}/health`, { signal: controller.signal, cache: "no-store" });
        clearTimeout(to);
        measured = Math.round(performance.now() - start);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        // >4000ms suggests a cold-start in progress — mark "slow" but not "offline"
        nextStatus = measured > 4000 ? "slow" : "live";
        failuresRef.current = 0;
        if (!cancelled) setLastOk(new Date());
      } catch (e) {
        failuresRef.current += 1;
        // Bumped from 2 → 3 consecutive failures before flipping to offline.
        // On flaky mobile networks, two random Cloudflare 5xx blips in a row
        // are common during a 10-minute admin session and shouldn't scream
        // "the system is down" at the user.
        if (failuresRef.current >= 3) {
          nextStatus = "offline";
        } else {
          nextStatus = "slow";
        }
      }
      if (cancelled) return;
      setStatus(nextStatus);
      setLatency(measured);
      // Faster polling when degraded so the user gets the recovery signal quickly.
      const next = nextStatus === "live" ? 30000 : nextStatus === "slow" ? 8000 : 4000;
      timer = setTimeout(ping, next);
    };
    ping();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (keepAliveTimer) clearTimeout(keepAliveTimer);
    };
  }, []);

  const palette = {
    checking: { dot: "bg-white/40", text: "text-white/50", label: "Checking…" },
    live:     { dot: "bg-green-400 shadow-[0_0_10px_rgba(74,222,128,0.6)]", text: "text-green-400", label: "Live" },
    slow:     { dot: "bg-yellow-400 shadow-[0_0_10px_rgba(251,191,36,0.6)]", text: "text-yellow-400", label: "Slow" },
    offline:  { dot: "bg-red-400 shadow-[0_0_12px_rgba(248,113,113,0.8)]", text: "text-red-400", label: "Offline" },
  };
  const p = palette[status] || palette.checking;

  const lastOkText = lastOk
    ? `Last reply: ${lastOk.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}`
    : "No successful reply yet";

  return (
    <div
      data-testid="admin-live-indicator"
      title={`${p.label}${latency ? ` · ${latency}ms` : ""}\n${lastOkText}`}
      className="fixed bottom-3 right-3 z-50 select-none"
    >
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-bg/90 backdrop-blur border border-white/10 text-[10px] font-display uppercase tracking-[0.22em]">
        <span className={`w-2 h-2 rounded-full ${p.dot} ${status === "live" ? "animate-pulse" : ""}`} />
        <span className={p.text}>{p.label}</span>
        {latency != null && status !== "offline" && (
          <span className="text-muted/60">{latency}ms</span>
        )}
      </div>
    </div>
  );
}
